import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { getDataFromDataKey } from "../../helpers/functions/getDataFromKey";
import BottomDrawer from "../Drawer/BottomDrawer";
import { v4 as uuidv4 } from "uuid";

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

function Filters({
  data,
  setFilteredData,
  filtersList,
  leadSource = null,
  handleSourceChange = null,
  localStorageKey = null,
  dropdownStyles = {},
  externalSelectedFilters = null,
  // Optional: reports the raw selectedFilters map (filterId -> {value: bool})
  // whenever it changes, so a caller can send the actual selected value(s)
  // to the backend — setFilteredData alone only returns the filtered rows,
  // never which value was picked.
  onSelectionChange = null,
}) {
  const activeFilter = filtersList[0]?.id;
  const [currentActiveFilter, setCurrentActiveFilter] = useState(activeFilter);

  const filtersValueFromLocalStorage = useMemo(() => {
    try {
      if (localStorageKey) {
        const storedFilters = localStorage.getItem(localStorageKey);
        if (storedFilters) {
          return JSON.parse(storedFilters);
        }
        return {};
      }
      return {};
    } catch (error) {
      console.error("Error parsing filters from localStorage:", error);
      return {};
    }
  }, [localStorageKey]);

  const [selectedFilters, setSelectedFilters] = useState(
    filtersValueFromLocalStorage,
  );

  useEffect(() => {
    if (!externalSelectedFilters) return;
    setSelectedFilters((prev) => {
      const updated = { ...prev, ...externalSelectedFilters };
      if (localStorageKey) {
        localStorage.setItem(localStorageKey, JSON.stringify(updated));
      }
      return updated;
    });
  }, [externalSelectedFilters, localStorageKey]);

  const [showMobileFilterModal, setShowMobileFilterModal] = useState(false);

  const filtersValuesList = useMemo(
    () =>
      filtersList.reduce((acc, filter) => {
        if (filter?.valueType === "array") {
          acc[filter.id] = filter?.filterValues;
          return acc;
        }
        if (filter?.valueType === "computed") {
          acc[filter.id] = filter?.filterValues;
          return acc;
        }
        if (filter?.valueType === "boolean") {
          acc[filter.id] = [true, false];
          return acc;
        }
        if (filter?.valueType === "date") {
          const values = data
            .map((item) => getDataFromDataKey(item, filter.key))
            .filter((value) => value && value?.toString()?.trim());
          const uniqueValues = [...new Set(values)];
          const sortedByRecency = uniqueValues.sort((a, b) => {
            const [dayA, monthA, yearA] = a.split("-");
            const [dayB, monthB, yearB] = b.split("-");
            return (
              new Date(yearB, monthB - 1, dayB).getTime() -
              new Date(yearA, monthA - 1, dayA).getTime()
            );
          });
          acc[filter.id] = filter?.limit
            ? sortedByRecency.slice(0, filter.limit)
            : sortedByRecency;
          return acc;
        }

        const values = data
          .map((item) => getDataFromDataKey(item, filter.key))
          .filter((value) =>
            value && value?.toString()?.trim() ? value : value,
          );
        const uniqueValues = [...new Set(values)];
        acc[filter.id] = uniqueValues.toSorted();
        return acc;
      }, {}),
    [data, filtersList],
  );

  const hasSelectedCurrentFilters = useMemo(() => {
    const filterValues = selectedFilters?.[currentActiveFilter] || null;
    return filterValues
      ? Object.keys(filterValues)?.some((value) => filterValues[value])
      : false;
  }, [selectedFilters, currentActiveFilter]);

  const hasSelectedAnyFilter = useMemo(() => {
    return Object.keys(selectedFilters)?.some((filterId) => {
      const filterValues = selectedFilters?.[filterId];
      return filterValues
        ? Object.keys(filterValues)?.some((value) => filterValues[value])
        : false;
    });
  }, [selectedFilters]);

  const handleFilterChange = (filterId, value) => {
    setSelectedFilters((prev) => {
      const filterValues = {
        ...prev,
        [filterId]: {
          ...prev[filterId],
          [value]: !prev?.[filterId]?.[value],
        },
      };
      if (localStorageKey) {
        localStorage.setItem(localStorageKey, JSON.stringify(filterValues));
      }

      return {
        ...prev,
        [filterId]: {
          ...prev[filterId],
          [value]: !prev?.[filterId]?.[value],
        },
      };
    });
  };

  const isAllSelected = (filterId) => {
    const values = filtersValuesList[filterId] || [];
    return (
      values.length > 0 &&
      values.every((value) => selectedFilters?.[filterId]?.[value] === true)
    );
  };

  const handleSelectAllChange = (filterId) => {
    const values = filtersValuesList[filterId] || [];
    const shouldSelectAll = !isAllSelected(filterId);

    setSelectedFilters((prev) => {
      const filterValues = values.reduce((acc, value) => {
        acc[value] = shouldSelectAll;
        return acc;
      }, {});
      const updated = {
        ...prev,
        [filterId]: filterValues,
      };
      if (localStorageKey) {
        localStorage.setItem(localStorageKey, JSON.stringify(updated));
      }
      return updated;
    });
  };

  const applyFilters = useCallback(() => {
    let results = structuredClone(data);

    if (leadSource && handleSourceChange) {
      results = handleSourceChange();
    }

    const filteredData = results.filter((item) => {
      return filtersList.every((filter) => {
        const filterId = filter.id;
        const filterValues = selectedFilters?.[filterId];
        const filterKey = filter.key;
        const filterType = filter.valueType;
        return filterValues &&
          Object.keys(filterValues).length > 0 &&
          Object.values(filterValues).some((val) => val)
          ? Object.keys(filterValues).some((value) => {
              const isChecked = filterValues?.[value];

              // Handle computed filters
              if (filterType === "computed") {
                return filter?.computeFn?.(item, value, isChecked);
              }

              const itemValue = getDataFromDataKey(item, filterKey);
              const isArray = Array.isArray(itemValue);
              if (isArray) {
                return isChecked && itemValue.some((val) => val === value);
              }

              if (filterType === "boolean") {
                return (
                  isChecked &&
                  itemValue?.toString()?.toLowerCase() ===
                    filter?.booleanOptions?.[value]?.filterValue
                      ?.toString()
                      ?.toLowerCase()
                );
              }

              return isChecked && itemValue === value;
            })
          : true;
      });
    });

    setFilteredData(filteredData);
  }, [
    data,
    selectedFilters,
    leadSource,
    handleSourceChange,
    filtersList,
    setFilteredData,
  ]);

  useEffect(() => {
    applyFilters();
  }, [selectedFilters, data]);

  useEffect(() => {
    applyFilters();
  }, [leadSource, data]);

  useEffect(() => {
    onSelectionChange?.(selectedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  const backEvent = (e) => {
    e.preventDefault();
    window.history.forward();
    if (isMobile) {
      if (showMobileFilterModal) {
        setShowMobileFilterModal(false);
      }
    }
  };

  useEffect(() => {
    window.addEventListener("popstate", backEvent);
    return () => window.removeEventListener("popstate", backEvent);
  }, []);

  const getValueForBoolean = (value, booleanOptions) => {
    if (value?.toString()?.toLowerCase() === "true") {
      return booleanOptions?.[true]?.filterLabel || "Yes";
    } else if (value?.toString()?.toLowerCase() === "false") {
      return booleanOptions?.[false]?.filterLabel || "No";
    } else {
      return value;
    }
  };

  const getFilterLabel = (filter, value) => {
    if (filter?.valueType === "boolean") {
      return getValueForBoolean(value, filter?.booleanOptions);
    }
    if (filter?.filterLabels && filter?.filterLabels[value]) {
      return filter?.filterLabels[value];
    }
    return value;
  };
  const handleClearFilters = () => {
    setSelectedFilters((prev) => ({
      ...prev,
      [currentActiveFilter]: {},
    }));
    if (localStorageKey) {
      localStorage.setItem(
        localStorageKey,
        JSON.stringify({
          ...JSON.parse(localStorage.getItem(localStorageKey) || "{}"),
          [currentActiveFilter]: {},
        }),
      );
    }
  };
  const handleClearAllFilters = () => {
    setSelectedFilters({});
    if (localStorageKey) {
      localStorage.setItem(localStorageKey, JSON.stringify({}));
    }
  };
  return (
    <>
      {isMobile ? (
        <>
          <button
            className=" btn bg-danger-subtle text-danger position-relative"
            style={{ height: "40px", width: "40px", borderRadius: "40px" }}
            onClick={() => setShowMobileFilterModal(true)}
            type="button"
          >
            <i class="bi bi-sliders2"></i>

            {hasSelectedAnyFilter && (
              <span class=" p-1 bg-danger position-absolute end-0 translate-middle rounded-circle"></span>
            )}
          </button>

          <BottomDrawer show={showMobileFilterModal}>
            <div
              className=" d-flex flex-column justify-content-between"
              style={{ height: "100%", maxHeight: "100%" }}
            >
              <div className=" d-flex justify-content-between align-items-center p-2 fw-semibold ">
                <span className=" text-uppercase">Filters</span>
                {hasSelectedCurrentFilters && (
                  <span
                    className=" text-danger cursor-pointer rounded-2 badge badge bg-danger-subtle"
                    onClick={handleClearFilters}
                  >
                    Clear
                  </span>
                )}
              </div>
              <div
                className=" d-flex flex-nowrap flex-grow-1"
                style={{ overflowY: "scroll" }}
              >
                <div
                  className="d-flex flex-wrap justify-content-start flex-column border-top  p-0 rounded-0"
                  style={{ height: "100%" }}
                >
                  {filtersList?.map((filter) => {
                    const selectedCount = selectedFilters?.[filter.id]
                      ? Object.keys(selectedFilters[filter.id]).filter(
                          (key) => selectedFilters[filter.id][key],
                        ).length
                      : 0;
                    return (
                      <div
                        key={filter.id}
                        className={` cursor-pointer px-2 py-1 border-bottom ${
                          currentActiveFilter === filter.id
                            ? "bg-primary-subtle text-primary fw-semibold"
                            : ""
                        }`}
                        onClick={() => setCurrentActiveFilter(filter.id)}
                      >
                        {filter.name}{" "}
                        {selectedCount ? (
                          <span
                            className=" fw-semibold text-dark"
                            style={{ fontSize: "0.65rem" }}
                          >
                            ({selectedCount})
                          </span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
                <div
                  className=" border rounded-0 flex-grow-1"
                  style={{ overflowY: "auto" }}
                >
                  {filtersList?.map((filter) => {
                    return (
                      <div
                        key={filter.id}
                        className={`filters-list-div  ${
                          currentActiveFilter === filter.id
                            ? "d-flex flex-wrap justify-content-start flex-column"
                            : "d-none"
                        }`}
                      >
                        {filtersValuesList[filter.id] &&
                          filtersValuesList[filter.id]?.length > 1 && (
                            <div className=" d-flex justify-content-start gap-2 align-items-center border-bottom  px-2 py-1 fw-semibold">
                              <input
                                type="checkbox"
                                id={`select-all-${filter.id}`}
                                className="m-0 text-sm"
                                style={{
                                  cursor: "pointer",
                                  width: "16px",
                                  height: "16px",
                                  maxHeight: "16px !important",
                                }}
                                checked={isAllSelected(filter.id)}
                                onChange={() => {
                                  handleSelectAllChange(filter.id);
                                }}
                              />
                              <label
                                htmlFor={`select-all-${filter.id}`}
                                className=""
                                style={{ cursor: "pointer" }}
                              >
                                Select All
                              </label>
                            </div>
                          )}
                        {filtersValuesList[filter.id] &&
                          filtersValuesList[filter.id]?.map((value) => {
                            const uniqueId = uuidv4();
                            const inputId = `filter-${filter.id}-${uniqueId}`;
                            return (
                              <div
                                key={value}
                                className=" d-flex justify-content-start gap-2 align-items-center border-bottom  px-2 py-1"
                              >
                                <input
                                  type="checkbox"
                                  id={inputId}
                                  className="m-0 text-sm"
                                  style={{
                                    cursor: "pointer",
                                    width: "16px",
                                    height: "16px",
                                    maxHeight: "16px !important",
                                  }}
                                  checked={
                                    selectedFilters?.[filter.id]?.[value] ===
                                    true
                                  }
                                  onChange={() => {
                                    handleFilterChange(filter.id, value);
                                  }}
                                />
                                <label
                                  htmlFor={inputId}
                                  className=""
                                  style={{ cursor: "pointer" }}
                                >
                                  {getFilterLabel(filter, value)}
                                </label>
                              </div>
                            );
                          })}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <div
                  className=" container-fluid align-items-center col-12 d-flex justify-content-center py-2 d-md-none shadow-lg"
                  // style={{
                  //   position: "fixed",
                  //   bottom: 0,
                  //   zIndex: 3,
                  //   background: "white",
                  //   left: 0,
                  // }}
                >
                  <span
                    className="col-6 text-center click-animation col-6 text-md fw-semibold text-uppercase"
                    onClick={() => {
                      setShowMobileFilterModal(false);
                    }}
                  >
                    Close
                  </span>
                  |
                  <span
                    className="col-6 text-center text-danger click-animation col-6 text-md fw-semibold text-uppercase"
                    onClick={() => {
                      handleClearAllFilters();
                      setShowMobileFilterModal(false);
                    }}
                  >
                    Clear ALL
                  </span>
                </div>
              </div>
            </div>
          </BottomDrawer>
        </>
      ) : (
        <div class="dropstart">
          <button
            className=" btn bg-danger-subtle text-danger"
            style={{ height: "40px", width: "40px", borderRadius: "40px" }}
            data-bs-toggle="dropdown"
            aria-expanded="false"
            data-bs-auto-close="outside"
            type="button"
          >
            <i class="bi bi-sliders2"></i>

            {hasSelectedAnyFilter && (
              <span class=" p-1 bg-danger position-absolute end-0 translate-middle rounded-circle"></span>
            )}
          </button>

          <div class="dropdown-menu p-0 " style={{ zIndex: 9999 }}>
            <div className=" d-flex justify-content-between align-items-center p-2 fw-semibold ">
              <span className=" text-uppercase text-md">Filters</span>
              {hasSelectedCurrentFilters && (
                <span
                  className=" text-danger cursor-pointer rounded-2 badge badge bg-danger-subtle"
                  onClick={handleClearFilters}
                >
                  Clear
                </span>
              )}
            </div>
            <div
              className=" d-flex flex-nowrap"
              style={{ width: "200px", height: "250px", ...dropdownStyles }}
            >
              <div
                className="d-flex flex-wrap justify-content-start flex-column border-top  p-0 rounded-0"
                style={{ height: "100%" }}
              >
                {filtersList?.map((filter) => {
                  const selectedCount = selectedFilters?.[filter.id]
                    ? Object.keys(selectedFilters[filter.id]).filter(
                        (key) => selectedFilters[filter.id][key],
                      ).length
                    : 0;
                  return (
                    <div
                      key={filter.id}
                      className={`text-sm cursor-pointer px-2 py-1 border-bottom ${
                        currentActiveFilter === filter.id
                          ? "bg-primary-subtle text-primary fw-semibold"
                          : ""
                      }`}
                      onClick={() => setCurrentActiveFilter(filter.id)}
                    >
                      {filter.name}{" "}
                      {selectedCount ? (
                        <span
                          className=" fw-semibold text-dark"
                          style={{ fontSize: "0.65rem" }}
                        >
                          ({selectedCount})
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              <div
                className=" border rounded-0 flex-grow-1"
                style={{ overflowY: "auto" }}
              >
                {filtersList?.map((filter) => {
                  return (
                    <div
                      key={filter.id}
                      className={`filters-list-div  ${
                        currentActiveFilter === filter.id
                          ? "d-flex flex-wrap justify-content-start flex-column"
                          : "d-none"
                      }`}
                    >
                      {filtersValuesList[filter.id] &&
                        filtersValuesList[filter.id]?.length > 1 && (
                          <div className=" d-flex justify-content-start gap-2 align-items-center border-bottom  px-2 py-1 fw-semibold">
                            <input
                              type="checkbox"
                              id={`select-all-${filter.id}`}
                              className="m-0 text-sm p-0"
                              style={{
                                cursor: "pointer",
                                width: "12px",
                                height: "12px",
                                maxHeight: "12px !important",
                              }}
                              checked={isAllSelected(filter.id)}
                              onChange={() => {
                                handleSelectAllChange(filter.id);
                              }}
                            />
                            <label
                              htmlFor={`select-all-${filter.id}`}
                              className=" text-sm"
                              style={{ cursor: "pointer" }}
                            >
                              Select All
                            </label>
                          </div>
                        )}
                      {filtersValuesList[filter.id] &&
                        filtersValuesList[filter.id]?.map((value) => {
                          const uniqueId = uuidv4();
                          const inputId = `filter-${filter.id}-${uniqueId}`;

                          return (
                            <div
                              key={value}
                              className=" d-flex justify-content-start gap-2 align-items-center border-bottom  px-2 py-1"
                            >
                              <input
                                type="checkbox"
                                id={inputId}
                                className="m-0 text-sm p-0"
                                style={{
                                  cursor: "pointer",
                                  width: "12px",
                                  height: "12px",
                                  maxHeight: "12px !important",
                                }}
                                checked={
                                  selectedFilters?.[filter.id]?.[value] === true
                                }
                                onChange={() => {
                                  handleFilterChange(filter.id, value);
                                }}
                              />
                              <label
                                htmlFor={inputId}
                                className=" text-sm"
                                style={{ cursor: "pointer" }}
                              >
                                {getFilterLabel(filter, value)}
                              </label>
                            </div>
                          );
                        })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default memo(Filters);
